import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject, Injectable } from '@angular/core';
import {
  filter,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  addErrorFiles,
  addPreviousSessionProcessingFiles,
  addPreviousSessionUploadingFiles,
  addUploadFiles,
  addUploadFilesSuccess,
  assetProcessingRequestFailure,
  assetProcessingSuccess,
  chunkUploadFailure,
  chunkUploadSuccess,
  clearUploaderAfterLogout,
  clearUploaderState,
  clearUploaderStateSuccess,
  destroyUploaderComponent,
  fileUploadSuccess,
  getProcessingFilesFromLocalStorage,
  getUploadingFilesFromLocalStorage,
  goToAssignMaterial,
  navigateToAssignMaterials,
  preventUploadingProcess,
  removeFile,
  removeFileSuccess,
  setCreatedAssets,
  setCurrentUploadId,
  setFilesToLocalStorage,
  setProcessingFilesToLocalStorage,
  setUploaderLoaded,
  startUploadNewFile,
  toggleViewUploader,
  updateProcessingFilesStatusesByProcessingResult,
  updateUploadProgress,
  updateUploadRemainingTime,
} from './uploader.actions';
import { Store } from '@ngrx/store';
import { uploaderFeature } from '@shared/components/uploader/store/uploader.feature';
import { concatLatestFrom, mapResponse } from '@ngrx/operators';
import { EMPTY, from, map, shareReplay, timer } from 'rxjs';
import {
  selectCurrentUploadingFile,
  selectIsAllAssetFinishedProcess,
  selectIsAllChunksUploading,
  selectPreparedReadyAssetIds,
  selectProcessingFiles,
  selectUploadingFiles,
} from '@shared/components/uploader/store/uploader.selectors';
import {
  KEY_PROCESSING_FILES_IN_LOCAL_STORAGE,
  KEY_UPLOADING_FILES_IN_LOCAL_STORAGE,
  PARALLEL_UPLOADS,
} from '@shared/components/uploader/constants/uploader.constant';
import { clampf, Truthy } from '@3dsource/utils';
import { Router } from '@angular/router';
import { mapCurrentChunkFile } from '@shared/components/uploader/helpers/map-current-chunk-file';
import { ToastrService } from 'ngx-toastr';
import { getErrorMessage } from '@shared/helpers/error-message';
import type { InputFile } from '@shared/components/uploader/interfaces/files.interface';
import {
  AssetCreationRequestListGQL,
  CreateAssetCreationRequestGQL,
} from '@graphql/default/services';
import { estimateRemainingTime } from '@shared/components/uploader/helpers/estimate-remaining-time';
import { mapProcessingUpdatedStatusFiles } from '@shared/components/uploader/helpers/map-processing-updated-status-files';
import { mapProcessingCreatedAssets } from '@shared/components/uploader/helpers/map-processing-created-assets';
import { MATERIALS_MAPPER } from '@shared/constants/routing-paths.constants';
import { UserService } from '@features/sign-in/services/user.service';

@Injectable()
export class UploaderEffects {
  private actions$ = inject(Actions);
  private uploadFileChunkGQL = inject(CreateAssetCreationRequestGQL);
  private assetCreationRequestGQL = inject(AssetCreationRequestListGQL);
  private store = inject(Store);
  private toastrService = inject(ToastrService);
  private router = inject(Router);
  private userService = inject(UserService);

  addUploadFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addUploadFiles),
      concatLatestFrom(() => this.store.select(uploaderFeature.selectFiles)),
      map(([{ files }, oldFiles]) => {
        if (!oldFiles?.length) {
          return { files, errorFiles: [] };
        }
        const newFiles: InputFile[] = [];
        const notUniqueFiles: InputFile[] = [];
        for (const file of files) {
          const findFile = oldFiles.find(
            (oldFile) =>
              oldFile.file.name === file.file.name &&
              oldFile.file.size === file.file.size,
          );
          if (Truthy(findFile)) {
            notUniqueFiles.push({ ...file, error: 'File already exists' });
          } else {
            newFiles.push(file);
          }
        }
        return { files: newFiles, errorFiles: notUniqueFiles };
      }),
      map((data) => addUploadFilesSuccess(data)),
    );
  });
  changeErrorFilesByAddUploadFilesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addUploadFilesSuccess),
      map(({ errorFiles }) => addErrorFiles({ files: errorFiles || [] })),
    );
  });
  clearPreviousSessionFilesByAddUploadFilesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addUploadFilesSuccess),
      map(() => addPreviousSessionUploadingFiles({ files: [] })),
    );
  });
  checkIfExistsFileToUploadByAddUploadFilesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addUploadFilesSuccess),
      concatLatestFrom(() => [
        this.store.select(uploaderFeature.selectFiles),
        this.store.select(uploaderFeature.selectCurrentUploadId),
      ]),
      filter(([, , uploadId]) => !uploadId),
      map(([, files]) => {
        const fileIndex = files?.findIndex(
          (file) => file.status === 'in_queue',
        );
        return setCurrentUploadId({
          uploadId: fileIndex !== -1 ? files[fileIndex].uploadId : null,
        });
      }),
    );
  });
  checkIfExistsNextFileToUploadByCreateAsset$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fileUploadSuccess, chunkUploadFailure),
      concatLatestFrom(() => [this.store.select(uploaderFeature.selectFiles)]),
      map(([, files]) => {
        const fileIndex = files.findIndex((file) => file.status === 'in_queue');
        const isFileExists = fileIndex !== -1;
        return setCurrentUploadId({
          uploadId: isFileExists ? files[fileIndex].uploadId : null,
        });
      }),
    );
  });
  checkIfExistsNextFileToUploadByRemoveFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(removeFileSuccess),
      concatLatestFrom(() => [this.store.select(uploaderFeature.selectFiles)]),
      map(([, files]) => {
        const fileIndex = files.findIndex((file) => file.status === 'in_queue');
        return setCurrentUploadId({
          uploadId: fileIndex !== -1 ? files[fileIndex].uploadId : null,
        });
      }),
    );
  });
  removeFileSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(removeFile),
      filter(({ input }) => input.status === 'uploading'),
      map(() => removeFileSuccess()),
    );
  });
  setUploaderProgressByUploadFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setCurrentUploadId),
      map(() => updateUploadProgress({ progress: 0 })),
    );
  });
  showAllAssetsProcessingSuccessNotification$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(updateProcessingFilesStatusesByProcessingResult),
        concatLatestFrom(() =>
          this.store.select(selectIsAllAssetFinishedProcess),
        ),
        filter(([, isAllAssetsProcessed]) => isAllAssetsProcessed),
        tap(() => {
          this.toastrService.success(
            'All assets successfully processed',
            'Assets processing is completed',
          );
        }),
      );
    },
    { dispatch: false },
  );
  createAssetBySetCurrentUploadId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setCurrentUploadId),
      filter(({ uploadId }) => !!uploadId),
      map(() => startUploadNewFile()),
    );
  });
  getUploadingFilesFromLocalStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUploadingFilesFromLocalStorage),
      map(() => {
        const filesLocalStorage = localStorage.getItem(
          KEY_UPLOADING_FILES_IN_LOCAL_STORAGE,
        );
        if (filesLocalStorage) {
          try {
            return JSON.parse(filesLocalStorage);
          } catch {
            return '';
          }
        }
      }),
      filter((files) => !!files),
      map((files) => addPreviousSessionUploadingFiles({ files })),
    );
  });
  getProcessingFilesFromLocalStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getProcessingFilesFromLocalStorage),
      map(() => {
        const filesLocalStorage = localStorage.getItem(
          KEY_PROCESSING_FILES_IN_LOCAL_STORAGE,
        );
        if (filesLocalStorage) {
          try {
            return JSON.parse(filesLocalStorage);
          } catch {
            return '';
          }
        }
      }),
      filter((files) => !!files),
      map((files) => addPreviousSessionProcessingFiles({ files })),
    );
  });
  beforeUnloadSaveUploadingFiles = createEffect(() => {
    return this.actions$.pipe(
      ofType(destroyUploaderComponent),
      map(() => setFilesToLocalStorage()),
    );
  });
  beforeUnloadSaveProcessingFiles = createEffect(() => {
    return this.actions$.pipe(
      ofType(destroyUploaderComponent),
      map(() => setProcessingFilesToLocalStorage()),
    );
  });
  getFilesFromLocalStorageByUploaderLoaded = createEffect(() => {
    return this.actions$.pipe(
      ofType(setUploaderLoaded),
      map(() => getUploadingFilesFromLocalStorage()),
    );
  });
  setUploadingFilesToLocalStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setFilesToLocalStorage),
        concatLatestFrom(() => this.store.select(selectUploadingFiles)),
        filter(([, files]) => files && files.length > 0),
        tap(([, files]) => {
          localStorage.setItem(
            KEY_UPLOADING_FILES_IN_LOCAL_STORAGE,
            JSON.stringify(files),
          );
        }),
      );
    },
    { dispatch: false },
  );
  setProcessingFilesToLocalStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setProcessingFilesToLocalStorage),
        concatLatestFrom(() => this.store.select(selectProcessingFiles)),
        filter(([, files]) => files && files.length > 0),
        tap(([, files]) => {
          localStorage.setItem(
            KEY_PROCESSING_FILES_IN_LOCAL_STORAGE,
            JSON.stringify(files),
          );
        }),
      );
    },
    { dispatch: false },
  );
  removeFilesFromLocalStorageByAddPreviousSessionFiles$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(addPreviousSessionUploadingFiles),
        filter(({ files }) => files.length === 0),
        tap(() =>
          localStorage.removeItem(KEY_UPLOADING_FILES_IN_LOCAL_STORAGE),
        ),
      );
    },
    { dispatch: false },
  );
  clearUploaderStateSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(clearUploaderState),
      tap(() => localStorage.removeItem(KEY_UPLOADING_FILES_IN_LOCAL_STORAGE)),
      map(() => clearUploaderStateSuccess()),
    );
  });
  toggleViewUploader$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(toggleViewUploader),
        map(() => document.querySelector('.uploader-header-panel')),
        filter((element) => !!element),
        tap((element) => element.classList.toggle('minimized')),
      );
    },
    { dispatch: false },
  );
  hideUploaderIfLogout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(startUploadNewFile),
      filter(() => !this.userService.isLoggedIn),
      tap(() =>
        this.userService.logout(
          'user clear application storage while uploading',
        ),
      ),
      map(() => toggleViewUploader()),
    );
  });
  startUploadNewFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(startUploadNewFile),
      filter(() => this.userService.isLoggedIn),
      concatLatestFrom(() => [this.store.select(selectCurrentUploadingFile)]),
      switchMap(([, currentUploadingFile]) => {
        const startTime = Date.now();
        const totalChunks = currentUploadingFile?.totalChunks ?? 0;
        const uploadId = currentUploadingFile?.uploadId ?? '';
        const chunkNumbers = Array.from(
          { length: totalChunks },
          (_, i) => i + 1,
        );
        const stop$ = this.actions$.pipe(
          ofType(preventUploadingProcess),
          take(1),
          shareReplay({ bufferSize: 1, refCount: true }),
        );

        return from(chunkNumbers).pipe(
          // eslint-disable-next-line @smarttools/rxjs/no-unsafe-takeuntil
          takeUntil(stop$),
          mergeMap(
            (chunkNumber) =>
              this.uploadFileChunkGQL
                .mutate(
                  {
                    input: mapCurrentChunkFile({
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      ...currentUploadingFile!,
                      chunkNumber,
                    }),
                  },
                  { context: { useMultipart: true } },
                )
                .pipe(
                  concatLatestFrom(() => [
                    this.store.select(selectCurrentUploadingFile),
                  ]),
                  filter(([, file]) => !file?.isCompleted),
                  mapResponse({
                    next: ([response]) =>
                      chunkUploadSuccess({
                        uploadId: uploadId || '',
                        chunkNumber,
                        remainingTime:
                          estimateRemainingTime(
                            startTime,
                            chunkNumber,
                            totalChunks,
                          ) / 1000,
                        isCompleted:
                          !!response.data?.createAssetCreationRequest.completed,
                      }),
                    error: (_error: unknown) =>
                      chunkUploadFailure({
                        error:
                          'Upload failed, please try uploading this file again',
                        uploadId: uploadId || '',
                      }),
                  }),
                  takeUntil(stop$),
                ),
            PARALLEL_UPLOADS,
          ),
        );
      }),
    );
  });
  setUploaderProgressByCreateAssetPartiallySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(chunkUploadSuccess),
      concatLatestFrom(() => [this.store.select(selectCurrentUploadingFile)]),
      map(([, file]) => {
        if (!file) {
          return updateUploadProgress({ progress: 0 });
        }
        const uploadedChunks = Object.values(file.readyChunks).filter(
          Boolean,
        ).length;
        const progress = +clampf(
          0,
          100,
          (uploadedChunks / file.totalChunks) * 100,
        ).toFixed(2);
        return updateUploadProgress({ progress });
      }),
    );
  });
  setTimeRemainingByCreateAssetSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        fileUploadSuccess,
        chunkUploadFailure,
        clearUploaderState,
        removeFileSuccess,
      ),
      map(() => updateUploadRemainingTime({ remainingTime: null })),
    );
  });
  setTimeRemainingByCreateAssetPartiallySuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(chunkUploadSuccess),
      map(({ remainingTime }) => updateUploadRemainingTime({ remainingTime })),
    );
  });
  checkFileUploadByChunkUploadSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(chunkUploadSuccess),
      concatLatestFrom(() => this.store.select(selectIsAllChunksUploading)),
      filter(([, isAllChunksUploaded]) => !!isAllChunksUploaded),
      map(([data]) => fileUploadSuccess({ uploadId: data.uploadId })),
    );
  });
  preventUploadingProcess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(clearUploaderState, chunkUploadFailure, removeFileSuccess),
      map(() => preventUploadingProcess()),
    );
  });
  preventUploadingProcessAfterLogout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(clearUploaderAfterLogout),
      map(() => preventUploadingProcess()),
    );
  });
  clearUploaderStateSuccessAfterLogout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(clearUploaderAfterLogout),
      map(() => clearUploaderStateSuccess()),
    );
  });
  goToEditMaterials$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(navigateToAssignMaterials),
        concatLatestFrom(() => [
          this.store.select(selectPreparedReadyAssetIds),
        ]),
        tap(([, assetIds]) => {
          const url = this.router.serializeUrl(
            this.router.createUrlTree([MATERIALS_MAPPER], {
              queryParams: { ...assetIds },
            }),
          );
          window.open(url, '_blank');
        }),
      );
    },
    { dispatch: false },
  );
  goToEditMaterials = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(goToAssignMaterial),
        concatLatestFrom(() => [
          this.store.select(uploaderFeature.selectCreatedAssets),
        ]),
        map(([{ uploadId }, assetIds]) => assetIds[uploadId]),
        filter((asset) => !!asset),
        tap((asset) => {
          const queryParam =
            asset.assetType === 'environment'
              ? { environments: asset.assetId }
              : { products: asset.assetId };
          const url = this.router.serializeUrl(
            this.router.createUrlTree([MATERIALS_MAPPER], {
              queryParams: { ...queryParam },
            }),
          );
          window.open(url, '_blank');
        }),
      );
    },
    { dispatch: false },
  );
  pollProcessingStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fileUploadSuccess, addPreviousSessionProcessingFiles),
      switchMap(() =>
        timer(0, 10000).pipe(
          concatLatestFrom(() => [
            this.store.select(uploaderFeature.selectFiles),
            this.store.select(uploaderFeature.selectCreatedAssets),
          ]),
          switchMap(([, stateFiles, stateAssets]) => {
            const processingStateFiles = stateFiles.filter(
              (file) => file.status === 'processing',
            );
            const stateProcessingFilesIds = processingStateFiles.map(
              (file) => file.uploadId,
            );
            if (stateProcessingFilesIds.length === 0) {
              return EMPTY;
            }
            return this.assetCreationRequestGQL
              .fetch({
                input: { requestIds: stateProcessingFilesIds },
              })
              .pipe(
                mapResponse({
                  next: (response) => {
                    const assetCreationRequestsResponsesList =
                      response.data.assetCreationRequestList
                        .assetCreationRequests;
                    const updatedFiles = mapProcessingUpdatedStatusFiles(
                      assetCreationRequestsResponsesList,
                      stateFiles,
                    );
                    const completedAssets = mapProcessingCreatedAssets(
                      assetCreationRequestsResponsesList,
                      stateAssets,
                    );
                    return assetProcessingSuccess({
                      files: updatedFiles,
                      createdAssets: completedAssets,
                    });
                  },
                  error: (error: unknown) =>
                    assetProcessingRequestFailure({
                      error: getErrorMessage(error).error,
                    }),
                }),
              );
          }),
        ),
      ),
    );
  });
  clearProcessingFilesFromLocalStorageByAddPreviousSessionFiles$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(addPreviousSessionProcessingFiles),
        tap(() =>
          localStorage.removeItem(KEY_PROCESSING_FILES_IN_LOCAL_STORAGE),
        ),
      );
    },
    { dispatch: false },
  );
  updateFilesStatusesByProcessingSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(assetProcessingSuccess),
      filter(({ files }) => files.length > 0),
      map(({ files }) =>
        updateProcessingFilesStatusesByProcessingResult({ files }),
      ),
    );
  });
  setCreatedAssetsByProcessingSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(assetProcessingSuccess),
      filter(({ createdAssets }) => Object.keys(createdAssets).length > 0),
      map(({ createdAssets }) => setCreatedAssets({ assets: createdAssets })),
    );
  });
  showAssetProcessingRequestFailureNotification$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(assetProcessingRequestFailure),
        tap(({ error }) => {
          this.toastrService.error(
            error,
            'Failed to update asset processing statuses',
          );
        }),
      );
    },
    { dispatch: false },
  );
}
